import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { useCompanyInfo, useUpdateN8nConfig } from '@/hooks/useSettings';
import { Workflow, Shield, AlertCircle, Info } from 'lucide-react';

interface FormValues {
  webhookUrl: string;
  hmacSecret: string;
  enabled: boolean;
}

export function N8nConfigForm() {
  const { data: company, isLoading } = useCompanyInfo();
  const update = useUpdateN8nConfig();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: { webhookUrl: '', hmacSecret: '', enabled: false },
  });

  useEffect(() => {
    if (company) {
      reset({
        webhookUrl: company.n8n?.webhookUrl ?? '',
        hmacSecret: company.n8n?.hmacSecret ?? '',
        enabled: company.n8n?.enabled ?? false,
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: FormValues) => {
    await update.mutateAsync({
      webhookUrl: values.webhookUrl.trim(),
      hmacSecret: values.hmacSecret.trim() || undefined,
      enabled: values.enabled,
    });
  };

  const enabled = watch('enabled');
  const webhookUrl = watch('webhookUrl');
  const hmacSecret = watch('hmacSecret');

  const shadowedGateways = [
    company?.hermes?.enabled ? 'Hermes' : null,
    company?.openclaw?.enabled ? 'OpenClaw' : null,
  ].filter(Boolean) as string[];

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Workflow size={18} />
        </div>
        <div className="flex-1">
          <CardTitle>n8n Workflow Orchestrator</CardTitle>
          <CardDescription>
            n8n menerima webhook dari Cloud Function, format pesannya, lalu
            kirim ke Baileys. Format pesan bisa diubah dari n8n tanpa deploy
            ulang. Ini gateway yang direkomendasikan — lihat{' '}
            <code className="font-mono text-caption">scripts/VPS_SETUP.md</code>.
          </CardDescription>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Chip variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Aktif' : 'Nonaktif'}
        </Chip>
        <span className="text-caption text-ink-subtle">
          {enabled
            ? 'Invoice baru akan dikirim via n8n'
            : 'Invoice tidak akan dikirim otomatis via n8n'}
        </span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Toggle
          checked={enabled}
          onChange={(v) => setValue('enabled', v, { shouldDirty: true })}
          label="Aktifkan n8n"
          description="Aktifkan setelah workflow di n8n sudah di-import dan Baileys sudah scan QR."
        />

        <Input
          label="Webhook URL"
          type="url"
          placeholder="https://n8n.yourdomain.com/webhook/invoice-whatsapp"
          hint="URL production webhook dari node Webhook di n8n. Wajib HTTPS — Cloud Functions tidak bisa kirim ke HTTP biasa."
          error={errors.webhookUrl?.message}
          {...register('webhookUrl', {
            validate: (v) => {
              if (enabled && !v.trim()) return 'Webhook URL wajib diisi kalau aktif';
              if (v.trim() && !v.startsWith('https://')) return 'Harus https://';
              return true;
            },
          })}
          disabled={isLoading}
        />

        <details className="rounded border border-line bg-canvas/30 px-3 py-2">
          <summary className="text-small font-medium text-ink cursor-pointer flex items-center gap-1.5 select-none">
            <Shield size={14} className="text-primary" />
            Advanced: HMAC signature
          </summary>
          <div className="mt-3">
            <Input
              label="HMAC Secret (opsional)"
              type="password"
              placeholder="Shared secret — n8n workflow yang verifikasi"
              hint={
                hmacSecret
                  ? 'Signature aktif — request include X-Ophth-Signature header'
                  : 'Kosongkan kalau workflow n8n tidak verifikasi signature'
              }
              {...register('hmacSecret')}
            />
          </div>
        </details>

        {enabled && !webhookUrl && (
          <div className="flex items-start gap-2 p-3 rounded bg-warning/5 border border-warning/30 text-small text-warning">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Webhook URL belum diisi. Invoice tidak akan terkirim sampai ini diisi.</span>
          </div>
        )}

        {enabled && shadowedGateways.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded bg-info/5 border border-info/30 text-small text-info">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {shadowedGateways.join(' dan ')} juga aktif, tapi n8n punya
              prioritas tertinggi — selama n8n aktif,{' '}
              {shadowedGateways.join(' dan ')} tidak akan dipakai sama sekali
              (bukan fallback). Matikan setelah n8n terbukti jalan.
            </span>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!isDirty}
          >
            Simpan konfigurasi
          </Button>
        </div>
      </form>
    </Card>
  );
}
